import ValueSourceFactory from "./ValueSourceFactory.js";
/**
 * This will resolve any ValueSources in the grouping. If ValueSources
 * exist, the original grouping object will be included in the new one
 * in the field 'template'.
 * 
 * The returned updater will allow notification of when a ValueSource
 * needs to be rerun. The return from the updater function will be another
 * function that should be run when the grouping and update events are no
 * longer needed.
 * 
 *   doneWithGrouping = updater( rebuildGrouping )
 * 
 * 
 * @param {*} objRef 
 * @param {*} grouping 
 * @param {*} context 
 * @returns Object containing { grouping, updater }
 */
async function resolve(objRef, grouping, context) {
  if (!Array.isArray(grouping?.sections)) {
    return {
      grouping
    };
  }

  // Clone group
  const grp = {
    ...grouping,
    sections: [...grouping.sections]
  };
  for (let n = 0; n < grouping.sections.length; n++) {
    const section = grouping.sections[n];
    const {
      values
    } = section;
    const sec = {
      ...section
    };
    grp.sections[n] = sec;
    if (Array.isArray(values)) {
      let onlyHideIfEmpty = true;
      sec.values = [];
      for (let vn = 0; vn < values.length; vn++) {
        const value = values[vn];
        if (value.type) {
          const vs = ValueSourceFactory.create(value);
          grp.template = grouping;
          if (vs) {
            // eslint-disable-next-line no-await-in-loop
            const arr = await vs.getValues(objRef, grouping, context);
            arr.forEach(e => sec.values.push(e));
            onlyHideIfEmpty = onlyHideIfEmpty && !arr.some(e => e.hideIfEmpty !== true);
          } else {
            // eslint-disable-next-line no-console
            console.error('Unknown Grouping Value Source Type', values.type);
          }
        } else {
          onlyHideIfEmpty = onlyHideIfEmpty && value.hideIfEmpty === true;
          sec.values.push(value);
        }
      }
      if (onlyHideIfEmpty) {
        sec.values = [];
      }
    }
  }
  return {
    grouping: grp,
    updater: getUpdater(objRef, grouping, context)
  };
}

/**
 * Returns an array of ValueSources in the Grouping. If grouping.template exists, it will
 * look for ValueSources there.
 * @param {*} objRef The object ref of the grouping owner
 * @param {*} grouping The current template grouping object, that has ValueSources
 * @param {*} context the context
 * @returns 
 */
function getValueSources(objRef, grouping, context) {
  const grp = grouping.template || grouping;
  const rtn = [];
  grp.sections?.forEach(section => {
    section.values?.forEach(v => {
      const vs = v.type ? ValueSourceFactory.create(v) : null;
      if (vs) {
        rtn.push(vs);
      }
    });
  });
  return rtn;
}
function getUpdater(objRef, grouping, context) {
  const arr = getValueSources(objRef, grouping, context);
  return createUpdater(arr.map(vs => {
    return vs.getUpdater(objRef, grouping, context);
  }).filter(v => v != null));
}

/**
 * Returns true if a ValueSource needs updating.
 * @param {*} objRef The object ref of the grouping owner
 * @param {*} grouping The current template grouping object, that has ValueSources
 * @param {*} currentGrouping the current grouping, with ValueSources expanded into Values
 * @param {*} context the context
 * @returns 
 */
async function needsUpdating(objRef, grouping, context) {
  const vs = getValueSources(objRef, grouping, context);
  for (let n = 0; n < vs.length; n++) {
    // eslint-disable-next-line no-await-in-loop
    const dirty = await vs[n].needsUpdating(objRef, grouping, context);
    if (dirty) {
      return true;
    }
  }
  return false;
}
class Updater {
  constructor(updaters) {
    this.updaters = updaters || [];
    this.cancels = null;
  }
  install = cb => {
    if (this.cancels) {
      // eslint-disable-next-line no-console
      console.warn('ValueSource updaters already installed');
      return () => {};
    }
    this.cancels = this.updaters.map(u => {
      return u(cb);
    });
    return this.uninstall;
  };
  uninstall = () => {
    if (this.cancels) {
      this.cancels.forEach(c => {
        if (c) {
          c();
        }
      });
      this.cancels = null;
    }
  };
}
function createUpdater(updaters) {
  const u = new Updater(updaters);
  return u.install;
}
export default {
  resolve,
  needsUpdating,
  getValueSources,
  getUpdater
};